/** Zoom for the original-document panes (review PDF / DOCX, fact-audit source): the pure
 *  step/format logic (unit-tested without a DOM) and the per-pane state hook. The pill UI
 *  lives in ZoomControls.tsx, which stays components-only for fast refresh. */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 3;
export const ZOOM_STEP = 0.25;

/** One step in or out, clamped to [ZOOM_MIN, ZOOM_MAX]. Rounded to the step grid so repeated
 *  clicks never accumulate float noise (1.2500000001) that would show up in the label. */
export function stepZoom(zoom: number, dir: 'in' | 'out'): number {
  const next = dir === 'in' ? zoom + ZOOM_STEP : zoom - ZOOM_STEP;
  const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
  return Math.round(clamped / ZOOM_STEP) * ZOOM_STEP;
}

export function formatZoom(zoom: number): string {
  return `${Math.round(zoom * 100)}%`;
}

/** The scroll container's geometry — all the anchor math needs (an HTMLElement fits). */
export interface ScrollBox {
  scrollLeft: number;
  scrollTop: number;
  scrollWidth: number;
  scrollHeight: number;
  clientWidth: number;
  clientHeight: number;
}

/** What the reader is looking at: the middle of the visible area, as a fraction of the
 *  whole document each way. Zoom scales the document evenly (every page, or the whole
 *  CSS-zoomed box), so the same fraction is the same spot at any zoom. */
export interface ViewAnchor {
  x: number;
  y: number;
}

export function viewAnchor(box: ScrollBox): ViewAnchor {
  return {
    x: box.scrollWidth > 0 ? (box.scrollLeft + box.clientWidth / 2) / box.scrollWidth : 0,
    y: box.scrollHeight > 0 ? (box.scrollTop + box.clientHeight / 2) / box.scrollHeight : 0,
  };
}

/** The scroll position that puts `anchor` back in the middle of the (resized) document. */
export function scrollForAnchor(box: ScrollBox, anchor: ViewAnchor): { left: number; top: number } {
  return {
    left: Math.max(0, anchor.x * box.scrollWidth - box.clientWidth / 2),
    top: Math.max(0, anchor.y * box.scrollHeight - box.clientHeight / 2),
  };
}

/** How long after a zoom click the pane keeps re-centring as the document finishes
 *  resizing (react-pdf redraws each page in its own effect, after ours). Any scroll the
 *  user starts ends it early. */
const KEEP_PLACE_MS = 1500;

/**
 * Per-pane zoom state (in memory only — resets with the page, like a PDF viewer's).
 *
 * Takes the pane's scroll container (required, so a new pane can't silently lose this)
 * and a zoom keeps the reader's place (#364): the
 * scroll position used to stay the same number of pixels while the document grew or
 * shrank, so the view jumped, further the deeper the reader was. The place is taken AT
 * THE CLICK, before the zoom: afterwards, zooming out near the end has already had its
 * scroll position cut back to the shorter document.
 */
export function useDocumentZoom(scrollRef: RefObject<HTMLElement | null>) {
  const [zoom, setZoom] = useState(1);
  const pending = useRef<{ anchor: ViewAnchor; until: number } | null>(null);

  const restore = useCallback(() => {
    const el = scrollRef.current;
    const p = pending.current;
    if (!el || !p) return;
    if (performance.now() > p.until) {
      pending.current = null;
      return;
    }
    const { left, top } = scrollForAnchor(el, p.anchor);
    el.scrollLeft = left;
    el.scrollTop = top;
  }, [scrollRef]);

  const change = useCallback(
    (step: (z: number) => number) => {
      const next = step(zoom);
      // Not a change (the "100%" pill at 100%, a button at its limit): nothing will resize,
      // so hold no place — a later redraw would otherwise pull the reader back to it.
      if (next === zoom) return;
      const el = scrollRef.current;
      if (el) pending.current = { anchor: viewAnchor(el), until: performance.now() + KEEP_PLACE_MS };
      setZoom(next);
    },
    [scrollRef, zoom],
  );
  const zoomIn = useCallback(() => change((z) => stepZoom(z, 'in')), [change]);
  const zoomOut = useCallback(() => change((z) => stepZoom(z, 'out')), [change]);
  const reset = useCallback(() => change(() => 1), [change]);

  // CSS-zoomed panes (Word, fact-audit text) have resized by now.
  useLayoutEffect(restore, [zoom, restore]);

  // The PDF pane resizes later, page by page: re-centre on each resize until it settles.
  // The user scrolling, dragging or pressing a key takes over and ends it.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const stop = () => {
      pending.current = null;
    };
    const inputs = ['wheel', 'pointerdown', 'touchstart', 'keydown'] as const;
    for (const t of inputs) el.addEventListener(t, stop, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(restore);
      for (const child of Array.from(el.children)) ro.observe(child);
    }
    return () => {
      for (const t of inputs) el.removeEventListener(t, stop);
      ro?.disconnect();
    };
  }, [scrollRef, restore]);

  return { zoom, zoomIn, zoomOut, reset, canIn: zoom < ZOOM_MAX, canOut: zoom > ZOOM_MIN };
}
